import { DIALOG_DATA } from '@angular/cdk/dialog';
import {
  AfterViewInit,
  Component,
  HostBinding,
  Inject,
  OnInit,
  Optional,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { SwUpdate } from '@angular/service-worker';
import { Contact } from '@app/contacts/models/contact';
import { ContactService } from '@app/contacts/services/contact.service';
import { BrandingField } from '@app/core/models/branding.models';
import { AppFeature } from '@app/core/models/config.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { AppVersionService } from '@app/core/services/app-version.service';
import { BrandingService } from '@app/core/services/branding.service';
import { UpdateService } from '@app/core/services/update.service';
import { ElectronService } from '@app/electron/electron.service';
import { VoicemailSettingsApiService } from '@app/preferences/services/voicemail-settings-api.service';
import { VoicemailSettingsFacadeService } from '@app/preferences/services/voicemail-settings-facade.service';
import { VoicemailSettingsStateService } from '@app/preferences/services/voicemail-settings-state.service';
import { delay } from 'rxjs';

import { SettingType } from '../../models/settings.models';
import { SettingsPage } from '../../models/settings-page';

@Component({
  selector: 'app-preference-layout',
  templateUrl: './preference-layout.component.html',
  styleUrls: ['./preference-layout.component.scss'],
  providers: [VoicemailSettingsFacadeService, VoicemailSettingsApiService, VoicemailSettingsStateService],
})
export class PreferenceLayoutComponent implements OnInit, AfterViewInit {
  @ViewChildren('page') pages: QueryList<SettingsPage> = new QueryList<SettingsPage>();

  protected readonly SettingType = SettingType;

  protected currentSelect: string = SettingType.Profile;
  protected currentPage?: SettingsPage;
  protected profile?: Contact;
  protected debugVersion?: string;
  protected panelItems: string[] = [];
  protected currentHover: string = '';
  protected readonly BrandingField = BrandingField;
  @HostBinding('style.--highlighted-color-left-side-menu-item') highlightedColorLeftSideMenuItem: string = '';
  constructor(
    private contactService: ContactService,
    protected appConfigService: AppConfigService,
    private brandingService: BrandingService,
    @Optional() @Inject(DIALOG_DATA) public data: SettingType | null,
    private swUpdate: SwUpdate,
    private electronService: ElectronService,
    protected updateService: UpdateService,
    private dialogRef: MatDialogRef<PreferenceLayoutComponent>,
    private appVersionService: AppVersionService,
    private translateService: AppTranslateService
  ) {
    this.appVersionService.getVersion().then((version) => {
      this.debugVersion = version;
    });
  }

  ngOnInit(): void {
    this.contactService.currentUser$.subscribe((contact) => {
      this.profile = contact;
    });

    this.brandingService.brandingData$.subscribe((brandingData) => {
      this.highlightedColorLeftSideMenuItem = brandingData[BrandingField.FieldHighlightColorLeftSideMenuItems];
    });

    this.appConfigService.hasOfficeManagerRole$.subscribe(() => {
      this.getPanels();
    });
  }

  ngAfterViewInit() {
    // Since currentPage is used with `ngIf` in our template and it starts with a value of `undefined`,
    // delay this observable to the next cycle of the run loop to allow for the view change detection
    // to stabilize and avoid the "Expression has changed after it was checked" error in development mode.
    // See https://blog.angular-university.io/angular-debugging/ for more info.
    this.pages.changes.pipe(delay(0)).subscribe(() => {
      // due to ngIf, each time just one page is visible on the screen, that's why we obtain it via `first`
      this.currentPage = this.pages.first;
    });

    if (this.data) {
      this.currentSelect = this.data;
    }
  }

  private getPanels() {
    this.panelItems = Object.values(SettingType);

    if (!this.appConfigService.isFeatureEnabled(AppFeature.EnableCalling)) {
      // Calling is disabled by account settings, so every calling-related panel is unreachable.
      const callingPanels: SettingType[] = [
        SettingType.Calling,
        SettingType.AnsweringRules,
        SettingType.BlockedNumbers,
        SettingType.Voicemail,
        SettingType.MusicOnHold,
        SettingType.CallRecordings,
      ];
      this.panelItems = this.panelItems.filter((panel) => !callingPanels.includes(panel as SettingType));
    }

    if (!this.appConfigService.features[AppFeature.CompanionMode] && !this.electronService.isRunningInElectron) {
      // removing whole Calling panel when Companion mode is disabled AND not running on desktop as it would be empty
      const callingIndex = this.panelItems.indexOf(SettingType.Calling);
      if (callingIndex !== -1) {
        this.panelItems.splice(callingIndex, 1);
      }
    }

    if (
      !this.appConfigService.features[AppFeature.DesktopDownloads] &&
      !this.appConfigService.features[AppFeature.MobileDownloads]
    ) {
      /* Removing the entire Apps panel because desktop and mobile app downloads are not allowed. */
      this.panelItems.splice(this.panelItems.indexOf(SettingType.Apps), 1);
    }

    if (
      !this.appConfigService.features[AppFeature.CallRecordingAnnouncement] ||
      !(this.contactService.currentUser?.scope?.includes('Office Manager') === true)
    ) {
      const callRecordingsIndex = this.panelItems.indexOf(SettingType.CallRecordings);
      if (callRecordingsIndex !== -1) {
        this.panelItems.splice(callRecordingsIndex, 1);
      }
    }

    if (!this.appConfigService.hasOfficeManagerRole) {
      this.panelItems.splice(this.panelItems.indexOf(SettingType.SpeedDial), 1);
    }
  }

  protected async save(page: SettingsPage) {
    try {
      await page.save();
      this.currentSelect = SettingType.Profile;
    } catch {
      // silently catching the exceptions, they should be handled in individual setting pages
      // it prevents returning to default page in case of error
    }
  }

  protected async onUpdate() {
    if (this.updateService.isUpdateRejected) {
      // the reason why we don't call checkUpdate is swUpdate.checkForUpdate() will return false if the user rejects
      // the update at the first time
      if (confirm(this.translateService.instant('PREF_NAV.NEW_VERSION_AVAILABLE'))) {
        window.location.reload();
      }
    } else {
      await this.updateService.checkUpdate(true);
    }
  }

  protected close() {
    this.dialogRef.close();
  }
}
